import { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  useTheme,
} from '@mui/material'
import api from '../../api/axiosInstance'
import Swal from 'sweetalert2'

const UserProfileSettingsPage = ({ userInfo }) => {
  const theme = useTheme()
  const dc = theme.palette.designTokens

  const [form, setForm] = useState({
    name: userInfo.name || '',
    email: userInfo.email || '',
    password: '',
    role: userInfo.role || 'user',
    isEmailVerified: userInfo.isEmailVerified ?? false,
  })
  const [saving, setSaving] = useState(false)

  const setField = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }))

  const saveChangesInfo = async () => {
    try {
      setSaving(true)
      const updateBody = {
        email: form.email,
        name: form.name,
        role: form.role,
        isEmailVerified: form.isEmailVerified,
      }
      if (form.password) {
        updateBody.password = form.password
      }
      await api.patch(`/users/${userInfo.id}`, updateBody)
      setForm((prev) => ({ ...prev, password: '' }))
      Swal.fire({
        icon: 'success',
        title: 'Kullanıcı Güncellendi !',
        text: 'Güncelleme başarılı şekilde gerçekleşti',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } catch (error) {
      console.log(error)
      Swal.fire({
        icon: 'error',
        title: 'Hatalı güncelleme işlemi !',
        text: error?.response?.data?.message || 'Bir hata oluştu',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } finally {
      setSaving(false)
    }
  }

  const cardSx = {
    background: dc.card,
    border: `1px solid ${dc.cardBorder}`,
    borderRadius: '20px',
    backdropFilter: 'none',
    p: '32px 40px 40px',
  }

  const fieldLabelSx = {
    fontSize: 12,
    fontWeight: 600,
    color: dc.label,
    mb: 1,
    display: 'block',
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: dc.field,
      borderRadius: '10px',
      fontSize: 15,
      color: dc.value,
      '& fieldset': { borderColor: dc.fieldBorder },
      '&:hover fieldset': { borderColor: dc.fieldBorder },
      '&.Mui-focused fieldset': { borderColor: dc.accent, borderWidth: '1px' },
      '&.Mui-disabled': {
        background: dc.field,
        '& fieldset': { borderColor: dc.fieldBorder },
      },
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: dc.muted,
      color: dc.muted,
    },
    '& .MuiInputBase-input': { paddingTop: '11px', paddingBottom: '11px' },
  }

  const field = (label, icon, key, opts = {}) => (
    <Box sx={opts.full ? { gridColumn: '1 / -1' } : undefined}>
      <Typography component="span" sx={fieldLabelSx}>
        {label}
      </Typography>
      <TextField
        fullWidth
        hiddenLabel
        size="small"
        select={opts.select}
        type={opts.type}
        placeholder={opts.placeholder}
        disabled={opts.disabled}
        value={form[key]}
        onChange={setField(key)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box component="span" sx={{ fontSize: 17, lineHeight: 1 }}>
                {icon}
              </Box>
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      >
        {opts.children}
      </TextField>
    </Box>
  )

  return (
    <Box sx={cardSx}>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', mb: '28px' }}>
        Kullanıcı Ayarları
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '20px 32px',
        }}
      >
        {field('Ad', '👤', 'name')}
        {field('E-posta', '✉️', 'email')}
        {field('Yeni Şifre', '🔒', 'password', {
          type: 'password',
          placeholder: 'Değiştirmek için girin',
        })}
        {field('Yetki', '💼', 'role', {
          select: true,
          children: [
            <MenuItem key="user" value="user">
              Kullanıcı
            </MenuItem>,
            <MenuItem key="admin" value="admin">
              Yönetici
            </MenuItem>,
          ],
        })}
        {field('E-posta Doğrulaması', '✅', 'isEmailVerified', {
          select: true,
          children: [
            <MenuItem key="true" value={true}>
              Onaylı
            </MenuItem>,
            <MenuItem key="false" value={false}>
              Onaylı Değil
            </MenuItem>,
          ],
        })}
      </Box>

      {/* Save action */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '32px' }}>
        <Box
          component="button"
          onClick={() => saveChangesInfo()}
          disabled={saving}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            px: '22px',
            py: '12px',
            border: 'none',
            borderRadius: '12px',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            color: dc.onGradient,
            background: dc.gradientBtn,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: saving ? 'none' : 'translateY(-1px)',
              boxShadow: saving ? 'none' : '0 4px 14px rgba(16,185,129,0.35)',
            },
          }}
        >
          💾 Değişiklikleri Kaydet
        </Box>
      </Box>
    </Box>
  )
}

export default UserProfileSettingsPage
